import { useState } from 'react';
import { RefreshCw, Lock, Unlock, Download, FileText, ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, EmptyState } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useCompanies,
  useClosings,
  useCreateOrRecalculateClosing,
  useFinalizeClosing,
  useReopenClosing,
  useExportClosing,
} from '../../../hooks/useFinancialOps';
import { useCardInvoicePendencies } from '../hooks/useCardInvoices';
import { RateioEntreEmpresasSection } from '../components/RateioEntreEmpresasSection';

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
}

function CardInvoicePendencias({ closingId }: { closingId: string }) {
  const { data } = useCardInvoicePendencies(closingId);
  const pendencies = data?.data ?? [];
  if (!pendencies.length) {
    return null;
  }
  return (
    <div className="text-xs border border-border rounded px-2 py-1 space-y-0.5">
      <p className="font-medium text-ink">Faturas de cartão pendentes nesta competência:</p>
      {pendencies.map((p) => (
        <p key={p.id}>
          <Link to={`/financeiro/faturas/${p.id}`} className="underline">
            {p.card.name}
          </Link>{' '}
          — {formatCurrency(p.totalAmount)} (
          {p.status === 'EXTRACTED' ? 'em revisão' : 'aguardando conciliação'})
        </p>
      ))}
    </div>
  );
}

export function FechamentoPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const { data, isLoading } = useClosings(selectedCompanyId, selectedYear);
  const calcClosing = useCreateOrRecalculateClosing();
  const finalizeClosing = useFinalizeClosing();
  const reopenClosing = useReopenClosing();
  const exportClosing = useExportClosing();

  const closings = data?.data ?? [];

  const closingByMonth = new Map<number, any>();
  for (const c of closings) {
    closingByMonth.set(c.month, c);
  }

  function handleExport(id: string) {
    exportClosing.mutate(id, {
      onSuccess: (res: any) => {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fechamento-${id}.json`;
        a.click();
        URL.revokeObjectURL(url);
      },
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Empresa
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink min-w-[200px]"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Ano
          </label>
          <input
            type="number"
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink w-24"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            min="2000"
            max="2100"
          />
        </div>
      </div>

      {!selectedCompanyId ? (
        <EmptyState
          title="Selecione uma empresa"
          body="Escolha uma empresa para ver o fechamento mensal."
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {MONTH_NAMES.map((monthName, idx) => {
            const month = idx + 1;
            const closing = closingByMonth.get(month);
            const isFuture = new Date(selectedYear, month - 1, 1) > new Date();

            return (
              <div
                key={month}
                className={`border border-border rounded-lg p-4 bg-surface space-y-3 ${isFuture ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-ink text-sm">{monthName}</h3>
                  {closing && (
                    <Badge tone={closing.status === 'FINALIZED' ? 'accent' : 'warning'}>
                      {closing.status === 'FINALIZED' ? 'Finalizado' : 'Rascunho'}
                    </Badge>
                  )}
                </div>

                {closing ? (
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between text-ink-secondary">
                      <span>Receitas</span>
                      <span className="text-accent font-medium">
                        {formatCurrency(closing.totalIncome)}
                      </span>
                    </div>
                    <div className="flex justify-between text-ink-secondary">
                      <span>Despesas</span>
                      <span className="text-danger font-medium">
                        {formatCurrency(closing.totalExpense)}
                      </span>
                    </div>
                    <div className="flex justify-between text-ink-secondary">
                      <span>Impostos</span>
                      <span className="font-medium">{formatCurrency(closing.taxAmount)}</span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-1 font-semibold text-ink">
                      <span>Resultado Líquido</span>
                      <span className={closing.netResult >= 0 ? 'text-accent' : 'text-danger'}>
                        {formatCurrency(closing.netResult)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-ink-tertiary">Sem dados</p>
                )}

                {closing && <CardInvoicePendencias closingId={closing.id} />}

                {!isFuture && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    <button
                      onClick={() =>
                        calcClosing.mutate({
                          companyId: selectedCompanyId,
                          month,
                          year: selectedYear,
                        })
                      }
                      className="flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary border border-border rounded px-2 py-1"
                      disabled={calcClosing.isPending || closing?.status === 'FINALIZED'}
                      title="Calcular / Recalcular"
                    >
                      <RefreshCw size={12} />
                      {closing ? 'Recalcular' : 'Calcular'}
                    </button>

                    {closing && closing.status === 'DRAFT' && (
                      <button
                        onClick={() => finalizeClosing.mutate(closing.id)}
                        className="flex items-center gap-1 text-xs text-ink-secondary hover:text-accent border border-border rounded px-2 py-1"
                        disabled={finalizeClosing.isPending}
                        title="Finalizar"
                      >
                        <Lock size={12} />
                        Finalizar
                      </button>
                    )}

                    {closing && closing.status === 'FINALIZED' && (
                      <button
                        onClick={() => reopenClosing.mutate(closing.id)}
                        className="flex items-center gap-1 text-xs text-ink-secondary hover:text-warning border border-border rounded px-2 py-1"
                        disabled={reopenClosing.isPending}
                        title="Reabrir"
                      >
                        <Unlock size={12} />
                        Reabrir
                      </button>
                    )}

                    {closing && (
                      <button
                        onClick={() => handleExport(closing.id)}
                        className="flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary border border-border rounded px-2 py-1"
                        disabled={exportClosing.isPending}
                        title="Exportar"
                      >
                        <Download size={12} />
                        Exportar
                      </button>
                    )}

                    {closing && (
                      <Link
                        to={`/financeiro/dre/${closing.id}`}
                        className="flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary border border-border rounded px-2 py-1"
                        title="Ver DRE do mês"
                      >
                        <FileText size={12} />
                        DRE
                      </Link>
                    )}

                    {closing && (
                      <Link
                        to={`/financeiro/relatorio-contador/${closing.id}`}
                        className="flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary border border-border rounded px-2 py-1"
                        title="Abrir relatório do contador"
                      >
                        <ClipboardList size={12} />
                        Relatório do Contador
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Rateio não é escopado por empresa: um saldo envolve sempre duas. */}
      <RateioEntreEmpresasSection year={selectedYear} />
    </div>
  );
}
